import type { Block, Blocks } from './block'
import { getTextFromChars } from './block'
import { isInline } from './translation-units'

/** One stretch of narration in one language, highlighted on `blockId`. `lang`
 * is set for studied-language text; unmarked text leaves it undefined so the
 * voice detects it. `offset` locates the text inside the block's own text; a
 * `label` is narration the page does not show. */
export type SpeechUnit = { type: 'speech'; blockId: string; text: string; lang?: string; offset: number; label?: true }
/** A break the narration itself makes: before a heading and at an `hr`
 * (`section`), and after a heading (`heading`). Speech never spans one. */
export type SpeechPause = { type: 'pause'; blockId: string; reason: 'section' | 'heading' }
export type NarrationSegment = SpeechUnit | SpeechPause
/** Structural words, in the listener's native language. */
export type SpeechLabels = { question: string; options: string; answer: string; explanation: string; criteria: string; hint: string }
/** Blocks that point at something else; they are read as the view labels them. */
export type SpeechReference = Extract<Block, { type: 'mention' | 'page' | 'pageLink' | 'pages' }>
export type SpeechOptions = {
  labels: SpeechLabels
  /** The page's studied language: cloze answers, accepted answers and expected
   * transcripts outside a language span are read in it. */
  studied?: string
  /** The text the view shows for a reference. */
  reference: (block: SpeechReference) => string
}

type Question = Extract<Block, { type: 'question' }>
type Run = { text: string; lang?: string }

/** Narration of a page in reading order: speech in one language per segment,
 * and the pauses the page structure makes. Every block type has exactly one
 * rule (`page-listening.spec.md` → Narration); a new type fails to compile here
 * until its narration is decided. A unit never mixes languages. */
export function narrate(blocks: Blocks, { labels, studied, reference }: SpeechOptions): NarrationSegment[] {
  const result: NarrationSegment[] = []
  let hints: { blockId: string; tip: string }[] = []
  // Offsets run across every unit of a block: a question's prompt, options and
  // explanation are consecutive texts of one element.
  const offsets = new Map<string, number>()

  const emit = (blockId: string, content: Run[], label?: string) => {
    const parts = tidy(content)
    if (parts.length === 0) return
    let offset = offsets.get(blockId) ?? 0
    if (label) result.push({ type: 'speech', blockId, text: label, offset, label: true })
    for (const part of parts) {
      result.push({ type: 'speech', blockId, text: part.text, ...(part.lang && { lang: part.lang }), offset })
      offset += part.text.length + 1
    }
    offsets.set(blockId, offset)
  }
  /** Emits one unit and the hints of the clozes it contains, in that order. */
  const pause = (blockId: string, reason: SpeechPause['reason']) => {
    const last = result.at(-1)
    if (!last) return
    // A section pause absorbs a heading pause right before it.
    if (last.type === 'pause') {
      if (reason === 'section') result[result.length - 1] = { type: 'pause', blockId, reason }
      return
    }
    result.push({ type: 'pause', blockId, reason })
  }
  const unit = (blockId: string, content: Run[], label?: string) => {
    emit(blockId, content, label)
    const pending = hints
    hints = []
    for (const hint of pending) emit(hint.blockId, [{ text: hint.tip }], `${labels.hint}:`)
  }

  const runsOf = (children: Blocks | undefined, lang?: string): Run[] => (children ?? []).flatMap(child => runs(child, lang))
  const answerRuns = (question: Question, lang?: string): Run[] => {
    switch (question.subtype) {
      case 'sc': return runsOf(question.opts[question.answer], lang)
      case 'mc': return question.answer.flatMap((index, position) => [...(position > 0 ? [{ text: ', ', lang }] : []), ...runsOf(question.opts[index], lang)])
      case 'sa': return [{ text: question.answer.join(' / '), lang: lang ?? studied }]
      case 'la': return [{ text: question.criteria.join('; '), lang }]
      case 'sr': return [{ text: question.answer.transcript, lang: lang ?? studied }]
      default: return question satisfies never
    }
  }

  /** Inline narration. Block-level types reached from inline content read
   * their children followed by a pause. */
  function runs(block: Block, lang?: string): Run[] {
    switch (block.type) {
      case 'text': return [{ text: block.text, lang }]
      case 'chars': return [{ text: getTextFromChars(block), lang }]
      case 'br': return [{ text: ' ', lang }]
      case 'l': return runsOf(block.children, block.lang)
      case 'cloze':
        if (block.tip) hints.push({ blockId: block.id, tip: block.tip })
        return runsOf(block.children, lang ?? studied)
      case 'question': return answerRuns(block, lang)
      case 'mention': case 'page': case 'pageLink': case 'pages': return [{ text: reference(block), lang }]
      case 'em': case 'a': case 'comment': case 'definition': return runsOf(block.children, lang)
      case 'review': return block.change === 'delete' ? [] : runsOf(block.children, lang)
      case 'diff': return block.kind === 'delete' ? [] : runsOf(block.children, lang)
      // A code block speaks only its caption.
      case 'pre': return block.caption ? [...runsOf(block.caption, lang), { text: ' ', lang }] : []
      // LaTeX, code, media and editorial notes are not speakable prose.
      case 'code': case 'criticComment': case 'comments': case 'audio': case 'file': case 'hr': case 'pageMeta': return []
      case 'pageTitle': return [{ text: block.title, lang }, { text: ' ', lang }]
      case 'img': return [...runsOf(block.caption ?? (block.alt ? [{ id: block.id, type: 'text', text: block.alt }] : []), lang), { text: ' ', lang }]
      case 'video': case 'preview': return [...runsOf(block.caption, lang), { text: ' ', lang }]
      case 'alert': return [{ text: block.title ?? '', lang }, { text: ' ', lang }, ...runsOf(block.children, lang), { text: ' ', lang }]
      case 'quote': return [...runsOf(block.children, lang), { text: ' ', lang }, ...runsOf(block.author, lang), { text: ' ', lang }]
      case 'p': case 'h': case 'li': case 'td': case 'tr': case 'root': case 'rule': case 'questionGroup': case 'list': case 'table': case 'col': case 'cols':
        return [...runsOf(block.children, lang), { text: ' ', lang }]
      default: return block satisfies never
    }
  }

  function question(block: Question) {
    unit(block.id, runsOf(block.question), `${labels.question}:`)
    if ('opts' in block) block.opts.forEach((option, index) => { unit(block.id, runsOf(option), index === 0 ? `${labels.options}: 1.` : `${index + 1}.`) })
    unit(block.id, answerRuns(block), `${block.subtype === 'la' ? labels.criteria : labels.answer}:`)
    unit(block.id, runsOf(block.explanation), `${labels.explanation}:`)
  }

  /** A container's inline children form one unit on the container; block
   * children are walked in order. */
  function container(id: string, children: Blocks | undefined) {
    let inline: Blocks = []
    const flush = () => {
      if (inline.length > 0) unit(id, runsOf(inline))
      inline = []
    }
    for (const child of children ?? []) {
      if (isInline(child)) inline.push(child)
      else {
        flush()
        walk(child)
      }
    }
    flush()
  }

  function walk(block: Block): void {
    switch (block.type) {
      case 'question':
        if (block.inline) unit(block.id, runs(block))
        else question(block)
        return
      case 'h':
        pause(block.id, 'section')
        container(block.id, block.children)
        pause(block.id, 'heading')
        return
      case 'hr':
        pause(block.id, 'section')
        return
      case 'p': case 'li': case 'td': case 'tr': case 'root': case 'rule': case 'questionGroup': case 'list': case 'table': case 'col': case 'cols': case 'definition':
        container(block.id, block.children)
        return
      case 'review': case 'diff':
        if (block.type === 'review' ? block.change !== 'delete' : block.kind !== 'delete') container(block.id, block.children)
        return
      case 'quote':
        container(block.id, block.children)
        container(block.id, block.author)
        return
      case 'alert':
        if (block.title) unit(block.id, [{ text: block.title }])
        container(block.id, block.children)
        return
      case 'video': case 'preview': case 'pre':
        container(block.id, block.caption)
        return
      case 'img': case 'pageTitle': case 'text': case 'chars': case 'br': case 'cloze': case 'mention': case 'page': case 'pageLink': case 'pages':
      case 'em': case 'a': case 'l': case 'comment':
        unit(block.id, runs(block))
        break
      case 'code': case 'criticComment': case 'comments': case 'audio': case 'file': case 'pageMeta':
        break
      default: block satisfies never
    }
  }

  for (const block of blocks) walk(block)
  if (result.at(-1)?.type === 'pause') result.pop()
  return result
}

/** Collapses whitespace, merges neighbours in one language and folds
 * punctuation-only runs into the run before them. */
function tidy(input: Run[]): Run[] {
  const out: Run[] = []
  for (const run of input) {
    const last = out.at(-1)
    if (last && (last.lang === run.lang || !/[\p{L}\p{N}]/u.test(run.text))) last.text += run.text
    else if (run.text.trim() || last) out.push({ ...run })
  }
  for (const run of out) run.text = run.text.replaceAll(/\s+/g, ' ').trim()
  return out.filter(run => run.text)
}
